import { buildUserAttendCampaigns } from '@/redux/campaign/handles/fetchManyByCampaignIdsAction/helpers'
import { useChat } from '@/redux/chat'
import { loadChatsByStatusAction, refreshChatsCountAction } from '@/redux/chat/actions'
import { buildChaFilters } from '@/redux/chat/handles/loadChatsByStatusAction/helpers'
import { useCompany } from '@/redux/company'
import { selectCompanyOwnerId } from '@/redux/company/selectors'
import { useDepartment } from '@/redux/department'
import { useUser } from '@/redux/user'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast, ToastOptions } from 'react-toastify'
import { BulkCloseJob, listBulkCloseJobs } from './api'

export const BULK_CLOSE_JOB_CREATED_EVENT = 'devchat:bulk-close-job-created'

export function BulkCloseJobNotifier() {
  const { t } = useTranslation()
  const [job, setJob] = useState<BulkCloseJob | null>(null)
  const lastToastState = useRef('')
  const completedJobRef = useRef('')
  const activeToastId = useRef('')
  const {
    chatDispatch,
    chatState: { chatFilters }
  } = useChat()
  const {
    userState: { userAuthenticated }
  } = useUser()
  const {
    companyState: { selectedCompany }
  } = useCompany()
  const {
    departmentState: { selectedDepartment }
  } = useDepartment()
  const companyOwnerId = useSelector(selectCompanyOwnerId)
  const chatFiltersKey = JSON.stringify(chatFilters)

  useEffect(() => {
    setJob(null)
    lastToastState.current = ''
    completedJobRef.current = ''
    return () => {
      if (activeToastId.current) toast.dismiss(activeToastId.current)
      activeToastId.current = ''
    }
  }, [selectedCompany, userAuthenticated?.id])

  useEffect(() => {
    const onCreated = (event: Event) => {
      const created = (event as CustomEvent<BulkCloseJob>).detail
      if (created?.companyId === selectedCompany) setJob(created)
    }
    window.addEventListener(BULK_CLOSE_JOB_CREATED_EVENT, onCreated)
    return () => window.removeEventListener(BULK_CLOSE_JOB_CREATED_EVENT, onCreated)
  }, [selectedCompany])

  useEffect(() => {
    if (!selectedCompany || !userAuthenticated?.id) return
    let active = true
    const poll = async () => {
      try {
        const latest = (await listBulkCloseJobs(selectedCompany))[0] || null
        if (!active) return
        setJob(latest)
        if (latest?.status === 'completed' && completedJobRef.current !== latest.id) {
          completedJobRef.current = latest.id
          chatDispatch(refreshChatsCountAction())
          chatDispatch(
            loadChatsByStatusAction.request({
              ownerId: companyOwnerId,
              campaignIds: buildUserAttendCampaigns({
                attendCampaigns: userAuthenticated.attendCampaigns,
                currentCompanyId: selectedCompany
              }),
              departmentId: selectedDepartment?.id || 'none',
              status: ['in_progress'],
              chatFilters: buildChaFilters(chatFilters),
              ...(!userAuthenticated.isAdmin && { operatorId: userAuthenticated.id })
            })
          )
        }
      } catch {
        // The server owns the job; a transient polling error must not erase its last known state.
      }
    }
    void poll()
    const timer = window.setInterval(
      () => {
        if (!document.hidden) void poll()
      },
      job?.status === 'completed' ? 60000 : 5000
    )
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [selectedCompany, userAuthenticated?.id, companyOwnerId, selectedDepartment?.id, chatFiltersKey, job?.status])

  useEffect(() => {
    if (!job || !selectedCompany || !userAuthenticated?.id) return
    const toastId = `bulk-close-${selectedCompany}-${userAuthenticated.id}`
    activeToastId.current = toastId
    const seenKey = `${toastId}:completed`
    if (job.status === 'completed' && sessionStorage.getItem(seenKey) === job.id) return
    const webhookIssues = job.items.some(item => item.webhook === 'failed' || item.webhook === 'unknown')
    const hasIssues = job.skipped > 0 || job.failed > 0 || webhookIssues
    const fingerprint = `${job.id}:${job.status}:${job.processed}:${job.closed}:${job.skipped}:${job.failed}:${webhookIssues}`
    if (lastToastState.current === fingerprint && toast.isActive(toastId)) return
    lastToastState.current = fingerprint
    const message = (
      <div role="status">
        <strong>
          {job.status === 'completed'
            ? hasIssues
              ? t('translation:bulkCloseUi.completedWithIssues')
              : t('translation:bulkCloseUi.finishedCount', '{{count}} finalizados', { count: job.closed })
            : t('translation:bulkCloseUi.finishing')}
        </strong>
        {job.status !== 'completed' && (
          <div>
            {job.processed}/{job.total}
          </div>
        )}
        {hasIssues && (
          <div>
            {t('translation:bulkCloseUi.finishedCount', '{{count}} finalizados', { count: job.closed })} ·{' '}
            {t('translation:bulkCloseUi.skippedCount', '{{count}} ignorados', { count: job.skipped })} ·{' '}
            {t('translation:bulkCloseUi.failedCount', '{{count}} falhas', { count: job.failed })}
            {webhookIssues && <div>{t('translation:bulkClose.webhookFailed')}</div>}
          </div>
        )}
      </div>
    )
    const options: ToastOptions = {
      type: job.status === 'completed' ? (hasIssues ? 'warning' : 'success') : 'info',
      autoClose: job.status === 'completed' && !hasIssues ? 8000 : false,
      closeOnClick: false,
      position: 'top-right'
    }
    if (toast.isActive(toastId)) toast.update(toastId, { render: message, ...options })
    else toast(message, { toastId, ...options })
    if (job.status === 'completed') sessionStorage.setItem(seenKey, job.id)
  }, [job, selectedCompany, userAuthenticated?.id, t])

  return null
}
